import request, { ResponseData } from '@/utils/axios';

export interface StoreResponse {
  platformDomainId: string;
  id: string;
  name: string;
  avatarUrl: string | null;
  backgroundUrl: string | null;
  phoneNumber: string | null;
  email: string;
  storeType: 'REAL_STORE' | 'VIRTUAL_STORE';
  businessState: 'BUILDING' | 'OPERATING';
  area: string;
  address?: string;
  locationInfo?: {
    address: string;
    latitude: number;
    longitude: number;
  };
}
/**
 * AuthRequest
 */
export interface AuthLoginParams {
  /**
   * 地区
   */
  area?: string;
  /**
   * 凭证校验方式
   */
  authMethod: AuthMethod;
  /**
   * 凭证
   * eg : 密码,验证码 (验证方式由 authMethod 决定)
   * 对于EMAIL_PWD直接使用密码字符串，对于EMAIL_PWD_OTP使用对象
   */
  certificate: { [key: string]: any } | string;
  /**
   * 凭证标识
   * eg : 账号名,邮箱 (验证方式由 authMethod 决定)
   * 对于EMAIL_PWD可以直接使用邮箱字符串
   */
  certificateIdentification?: { [key: string]: any } | string;
  /**
   * 平台
   */
  details?: { [key: string]: any };
  /**
   * 用户的设备信息
   */
  device: UserDevice;
  /**
   * 用什么身份登录
   */
  identityScope: IdentityScope;
  [property: string]: any;
}

/**
 * 凭证校验方式
 */
export enum AuthMethod {
  AppleAuthCodeFlow = "APPLE_AUTH_CODE_FLOW",
  EmailCode = "EMAIL_CODE",
  EmailPwd = "EMAIL_PWD",
  EmailPwdOtp = "EMAIL_PWD_OTP",
  GoogleAuthCodeFlow = "GOOGLE_AUTH_CODE_FLOW",
  Inner = "INNER",
  OtpCode = "OTP_CODE",
  RandomTime = "RANDOM_TIME",
  TokenReLogin = "TOKEN_RE_LOGIN",
  UsernamePwd = "USERNAME_PWD",
  UsernamePwdOtp = "USERNAME_PWD_OTP",
}

/**
 * 用户的设备信息
 *
 * UserDevice
 */
export interface UserDevice {
  appVersion?: string;
  deviceId?: string;
  deviceType?: string;
  [property: string]: any;
}

/**
 * 用什么身份登录
 */
export enum IdentityScope {
  Admin = "admin",
  Author = "author",
  Emp = "emp",
  Gm = "gm",
  Player = "player",
  Store = "store",
}

export interface StoreQueryParams {
  page: {
    page: number;
    pageSize: number;
  };
  [key: string]: any;
}

export const DEFAULT_STORE_PAGE_PARAMS: StoreQueryParams = {
  page: {
    page: 0,
    pageSize: 12
  }
};
const sendApi = {
  'login': '/uc/auth/login/send/code',
  'forget': '/uc/personal/force/change/password/send/code',
  'personal': '/uc/personal/send/code',
  'register': '/uc/register/send/code'
}
    const validApi = {
      'login': '/uc/auth/login/code/check', // 登录验证码验证
      'forget': '/uc/personal/force/change/password/code/check', // 忘记密码验证码验证
      'personal': '/uc/personal/code/check', // 个人信息修改验证码验证
      'register': '/uc/register/verification/code'// 注册验证码验证
    }

/**
 * 三方登录授权信息
 */
export interface ThirdPartyAuthorization {
  /**
   * 授权码
   */
  code: string;
  /**
   * 重定向URI
   */
  redirectUri: string;
  [property: string]: any;
}

/**
 * 三方登录请求参数
 */
export interface ThirdPartyLoginParams {
  /**
   * 认证方法
   * Google: GOOGLE_AUTH_CODE_FLOW
   * Apple: APPLE_AUTH_CODE_FLOW
   */
  authMethod: AuthMethod.GoogleAuthCodeFlow | AuthMethod.AppleAuthCodeFlow;
  /**
   * 身份作用域
   */
  identityScope: string;
  /**
   * 授权信息
   */
  authorization: ThirdPartyAuthorization;
  /**
   * 业务类型
   * AccountNotExistRegister: 账户不存在时注册
   * Login: 登录
   */
  bizType: string[];
  /**
   * 设备信息
   */
  device: UserDevice;
  [property: string]: any;
}

/**
 * 登录的用户信息
 */
export interface LoginUser {
  avatarUrl?: string;
  email?: string;
  id?: string;
  name?: string;
  nickname?: string;
  [property: string]: any;
}

/**
 * 登录成功响应数据
 */
export interface LoginSuccess {
  /**
   * Token有效时长（秒）
   */
  expiresIn?: number;
  /**
   * token 生成时间
   */
  issuedAt?: number;
  /**
   * 刷新时使用的Token
   */
  refreshToken?: string;
  /**
   * 登录成功后返回的Token
   */
  token: string;
  /**
   * 登录的用户信息
   */
  user?: LoginUser;
  [property: string]: any;
}

/**
 * 申请数据
 *
 * AuditInfo
 */
export interface AuditInfo {
    /**
     * 实例
     */
    auditId?: string;
    /**
     * 理由
     */
    reason?: string;
    /**
     * 状态
     */
    state?: State;
    /**
     * 时间
     */
    time?: number;
    [property: string]: any;
}

/**
 * 状态
 */
export enum State {
    Approved = "APPROVED",
    Pending = "PENDING",
    Rejected = "REJECTED",
    Revoke = "REVOKE",
}

/**
 * com.anzir.uc.service.provider.domain.identity.store.repo.TimePeriod
 *
 * TimePeriod
 */
export interface TimePeriod {
    end?: number;
    start?: number;
    [property: string]: any;
}

/**
 * 营业状态: 营业中
 */
export enum BusinessState {
    Building = "BUILDING",
    Closed = "CLOSED",
    Operating = "OPERATING",
    Paused = "PAUSED",
}

/**
 * com.anzir.uc.service.provider.domain.identity.store.repo.CooperationModel
 *
 * CooperationModel
 */
export interface CooperationModel {
    /**
     * 合作ID
     */
    cooperationId?: number;
    /**
     * 合作类型：直营、代理、加盟等
     */
    cooperationType?: number;
    /**
     * 备注信息
     */
    remarks?: string;
    [property: string]: any;
}

/**
 * 自定义标签
 * 可用作可见性规则
 *
 * MapSetString
 */
export interface MapSetString {
    key?: string[];
    [property: string]: any;
}

/**
 * 地理位置
 * 忽略json转换
 *
 * GeoJsonPoint
 */
export interface GeoJsonPoint {
    x?: number;
    y?: number;
    [property: string]: any;
}

/**
 * 店铺地理位置
 *
 * LocationInfo
 */
export interface LocationInfo {
    /**
     * 地址
     */
    address?: string;
    /**
     * 城市
     */
    city?: string;
    /**
     * 国家
     */
    country?: string;
    /**
     * 坐标(维度), ±90
     */
    latitude?: number;
    /**
     * 坐标(经度) ±180
     */
    longitude?: number;
    [property: string]: any;
}

/**
 * IdentityScope
 */
export enum Scope {
    Admin = "admin",
    Author = "author",
    Emp = "emp",
    Gm = "gm",
    Player = "player",
    Store = "store",
}

/**
 * 社交账号
 *
 * MapString
 */
export interface MapString {
    key?: string;
    [property: string]: any;
}

/**
 * 店铺类型
 */
export enum StoreType {
    RealStore = "REAL_STORE",
    VirtualStore = "VIRTUAL_STORE",
}

/**
 * 响应数据
 *
 * StoreIdentityEntity
 */
export interface StoreIdentityEntity {
    /**
     * 地址
     */
    address?: string;
    /**
     * 地区: 默认全球
     */
    area?: string;
    /**
     * 申请数据
     */
    auditInfo?: AuditInfo;
    /**
     * 店铺头像URL
     */
    avatarUrl?: string;
    /**
     * 背景图
     */
    backgroundUrl?: string;
    /**
     * 业务域
     * 业务分类
     */
    bizDomainIds?: string[];
    /**
     * 营业时间
     */
    businessHours?: TimePeriod[];
    /**
     * 经营范围: 线下、线上、无GM
     */
    businessScope?: string[];
    /**
     * 营业状态: 营业中
     */
    businessState?: BusinessState;
    /**
     * 城市
     */
    city?: string;
    /**
     * 店铺关闭原因
     */
    closeReason?: string;
    /**
     * 合作模式
     */
    cooperationModel?: CooperationModel[];
    /**
     * 国家
     */
    country?: string;
    /**
     * 创建时间。
     */
    createTime?: number;
    /**
     * 创建者身份。
     */
    creatorIdentity?: string;
    /**
     * 创建者名称（一般指身份名称）
     */
    creatorName?: string;
    /**
     * 创建者代理人
     */
    creatorProxy?: string;
    /**
     * 创建者代理人名称（一般指身份名称）
     */
    creatorProxyName?: string;
    /**
     * 自定义标签
     * 可用作可见性规则
     */
    customTags?: MapSetString;
    /**
     * 数据契约id。
     */
    dataModelId?: string;
    /**
     * 数据契约版本号
     */
    dataModelVersion?: string;
    /**
     * 描述
     */
    description?: string;
    /**
     * 店铺邮箱
     */
    email?: string;
    /**
     * 店铺环境描述
     */
    environmentDescription?: string;
    /**
     * 店铺配套设施描述
     */
    facilitiesDescription?: string;
    id?: string;
    /**
     * 店铺支持的语言
     */
    languages?: string[];
    /**
     * 地理位置
     * 忽略json转换
     */
    location?: GeoJsonPoint;
    /**
     * 店铺地理位置
     */
    locationInfo?: LocationInfo;
    /**
     * 修改者身份
     */
    menderIdentity?: string;
    /**
     * 修改者名称
     */
    menderName?: string;
    /**
     * 修改代理人
     * 运营者账号ID
     */
    menderProxy?: string;
    /**
     * 修改者代理人一般指身份名称
     */
    menderProxyName?: string;
    /**
     * 店铺名称
     */
    name?: string;
    /**
     * 当前数据归属方
     * eg: 如果当前数据是身份数据,则表示身份数据的归属方
     * eg: 如果当前数据是普通数据,则表示该数据的归属方,一般是创建人及创建人的所属方
     * <p>
     */
    owners?: string[];
    /**
     * 店铺电话
     */
    phoneNumber?: string;
    /**
     * 平台领域
     * 单字母，一般服务端配置，设置时手动写入
     * 必须
     */
    platformDomainId: string;
    /**
     * IdentityScope
     */
    scope?: Scope;
    /**
     * 店铺提供的服务项目描述
     */
    servicesDescription?: string;
    /**
     * 社交账号
     */
    socialAccount?: MapString;
    /**
     * 状态.
     */
    state?: number;
    /**
     * 店铺类型
     */
    storeType?: StoreType;
    /**
     * 主体名称
     */
    subjectId?: string;
    /**
     * 更新时间。
     */
    updateTime?: number;
    /**
     * 版本。
     */
    version?: number;
    /**
     * 权重
     */
    weight?: number;
    [property: string]: any;
}

/**
 * 店铺详情响应
 */
export interface StoreDetailResponse {
    /**
     * 响应状态码(1000=成功，-1000=失败，-99=异常）
     */
    code?: number;
    /**
     * 响应数据
     */
    data?: StoreIdentityEntity;
    /**
     * 响应状态消息
     */
    message?: string;
    [property: string]: any;
}

export interface Subject {
  id?: string;
  nickname?: string;
  name?: string;
  backgroundImage?: string;
  icon?: string;
  birthday?: string;
  sex?: string;
  personalSignature?: string;
  introduction?: string;
  area?: string;
  platformDomainId?: string;
  socialAccount?: Record<string, string>;
  createTime?: number;
  updateTime?: number;
  state?: number;
}

export interface AuthorIdentityResponse {
  code: number;
  message: string;
  data: Array<{
    id: string;
    scope: string;
    subjectId: string;
    signed: number;
    // ... other fields
  }>;
}

export interface LogoffSendCodeResponse {
  code: number;
  message: string;
  data: boolean;
}

export interface LogoffCodeCheckResponse {
  code: number;
  message: string;
  data: boolean;
}

export interface LogoffResponse {
  code: number;
  message: string;
  data: boolean;
}

// User Center API
export const ucApi = {
  // 店铺匿名查询
  store: {
    // 获取店铺列表
    top: (params: StoreQueryParams = DEFAULT_STORE_PAGE_PARAMS): Promise<ResponseData<StoreResponse[]>> => {
      return request({
        url: '/uc/identity/store/top',
        method: 'POST',
        data: params
      });
    },

    // 获取店铺详情
    detail: (id: string): Promise<ResponseData<StoreIdentityEntity>> => {
      return request({
        url: `/uc/identity/store/${id}`,
        method: 'GET'
      });
    },
  },

  // 主体相关
  subject: {
    // 更新主体信息
    update: (data: Partial<Subject>): Promise<ResponseData<Subject>> => {
      return request({
        url: '/uc/identity/subject/update',
        method: 'POST',
        data
      });
    },
  },

  // 认证相关
  auth: {
    // 验证邮箱是否存在
    hasEmail:(params:{email:string}):Promise<ResponseData>=>{
      return request({
        url: `/uc/auth/has/email?email=${encodeURIComponent(params.email)}`,
        method: 'GET'
      })
    },
    // 查询Email OTP设置
    emailAuth:(params:{email:string}):Promise<ResponseData>=>{
      return request({
        url: `/uc/personal/email/auth?email=${encodeURIComponent(params.email)}`,
        method: 'GET'
      })
    },
    // 查询注册邮箱是否合规
    emailLegal:(params:{email:string}):Promise<ResponseData>=>{
      return request({
        url: `/uc/register/register/email/legal?email=${encodeURIComponent(params.email)}`,
        method: 'GET'
      })
    },
    // 最后一次注销信息 /uc/register/last/logoff
    lastLogoff: (params:{email:string}): Promise<ResponseData> => {
      return request({
        url: `/uc/register/last/logoff?email=${encodeURIComponent(params.email)}`,
        method: 'GET'
      })
    },
    // 登录
    login: (params: AuthLoginParams): Promise<ResponseData> => {
      return request({
        url: '/uc/auth/login',
        method: 'POST',
        data: params
      });
    },

    // 用户注册合法性检查 /uc/register/check/register
    checkRegister: (params: {
      account: string;
      pwd: string;
      verificationCode: string;
      authMethod: string;
      scope: string;
      details: {
        nickname: string | null;
        sex: string | null;
        email: string;
        birthday: string | null;
        password: string;
        icon: string;
      };
      device: {
        deviceId: string;
        deviceType: string;
        appVersion: string;
      };
    }): Promise<ResponseData> => {
      return request({
        url: '/uc/register/check/register',
        method: 'POST',
        data: params
      });
    },
    // 用户注册 /uc/register
    register: (params: {
      account: string;
      pwd: string;
      verificationCode: string;
      authMethod: string;
      scope: string;
      details: {
        nickname: string | null;
        sex: string | null;
        email: string;
        birthday: string | null;
        password: string;
        icon: string;
      };
      device: {
        deviceId: string;
        deviceType: string;
        appVersion: string;
      };
    }): Promise<ResponseData> => {
      return request({
        url: '/uc/register',
        method: 'POST',
        data: params
      });
    },
    // 发送验证码
    codeSend: (params: { email: string ,name:string,type:string}): Promise<ResponseData> => {
      return request({
        url: sendApi[params.type],
        method: 'POST',
        data: params
      });
    },
    // 获取用户基础信息 /uc/personal/detail
    detail: (): Promise<ResponseData> => {
      return request({
        url: '/uc/personal/detail',
        method: 'GET',
      });
    },
    // 验证码验证
    codeVerify: (params: {
      code: string;
      email: string;
      type: string;
    }): Promise<ResponseData> => {
      return request({
        url: validApi[params.type],
        method: 'POST',
        data: params
      });
    },
    // 使用验证码重置密码
    resetPasswordWithCode: (params: {
      email: string;
      code: string;
      password: string;
    }): Promise<ResponseData> => {
      return request({
        url: '/uc/personal/force/change/password',
        method: 'POST',
        data: params
      });
    },
    // 获取用户详细信息
    getUserDetail: () => {
      return request({
        url: '/uc/personal/detail',
        method: 'POST',
      });
    },
    // 第三方登录
    thirdPartyLogin: (params: ThirdPartyLoginParams): Promise<ResponseData<LoginSuccess>> => {
      return request({
        url: '/uc/auth/third-party',
        method: 'POST',
        data: params
      });
    },
    // 获取用户所有身份
    getUserIdentities: (subjectId: string): Promise<ResponseData> => {
      return request({
        url: `/uc/personal/identities/${subjectId}`,
        method: 'GET'
      });
    },
    // 密码合法性验证
    checkPasswordLegal: (params: {
      subjectId: string;
      password: string
    }): Promise<ResponseData> => {
      return request({
        url: '/uc/auth/account/legal/password',
        method: 'GET',
          params
      });
    },
    // 更新登录密码
    changePassword: (params: {
      newPassword: string;
      oldPassword: string;
    }): Promise<ResponseData> => {
      return request({
        url: '/uc/personal/change/password',
        method: 'POST',
        data: params
      });
    }
  },

  // 上传文件相关
  upload: {
    // 获取上传 URL
    getUploadUrl: (params: { bucket: string; suffix: string; path: string }): Promise<ResponseData> => {
      return request({
        url: '/uc/upload/url',
        method: 'POST',
        data: params
      });
    }
  },

  // 查询用户身份列表
  identity: {
    // 根据身份类型和主题ID查询身份列表
    queryByScopeAndSubject: (params: { scope: string; subjectId: string }) => {
      return request<AuthorIdentityResponse>({
        url: '/uc/identity/scope/subject',
        method: 'GET',
        params
      });
    },
    
    // 获取签约作者数量
    getAuthorCount: (): Promise<ResponseData<number>> => {
      return request({
        url: '/uc/identity/author/count',
        method: 'POST',
        data: {
          match: {
            op: "AND",
            statements: [
              {
                fieldName: "signed",
                symbol: "EQUAL",
                value: 1
              }
            ]
          }
        }
      });
    }
  },

  // 简单用户信息查询
  user: {
    // 根据身份ID获取简单用户信息(匿名)
    getSimpleInfo: (params: { identityId: string }): Promise<ResponseData<SimpleUserInfo>> => {
      return request({
        url: '/uc/personal/simple/user',
        method: 'GET',
        params
      });
    }
  },

  // 账号注销相关
  personal: {
    // 发送注销验证码
    logoffSendCode: (params: { name: string,email:string }): Promise<ResponseData<boolean>> => {
      return request({
        url: '/uc/personal/logoff/send/code',
        method: 'POST',
        data: params
      });
    },

    // 验证注销验证码
    logoffCodeCheck: (params: { email: string; code: string }): Promise<ResponseData<boolean>> => {
      return request({
        url: '/uc/personal/logoff/code/check',
        method: 'POST',
        data: params
      });
    },

    // 执行注销操作
    logoff: (params: {subjectId: string; email: string; code: string }): Promise<ResponseData<boolean>> => {
      return request({
        url: '/uc/personal/logoff',
        method: 'POST',
        data: params
      });
    }
  }
};

// 简单用户信息接口
export interface SimpleUserInfo {
  subjectId: string;      // 主体ID
  identityId: string;     // 身份ID
  identityType: string;   // 身份类型
  nickname: string;       // 用户昵称
  icon?: string;          // 用户头像URL
  penName?: string;       // 笔名
  signed?: boolean;       // 是否签约作者
  introduction?: string;  // 用户简介
  anzirNo?: string;       // Anzir 编号
  email?: string;         // 用户邮箱
  socialAccount?: {       // 社交账号
    linkedin?: string;
    x?: string;
    instagram?: string;
    youtube?: string;
    discord?: string;
    [key: string]: string;
  };
  backgroundImage?: string; // 背景图片URL
  detail?: any;           // 详细信息
}

/**
 * 作者数量响应
 */
export interface AuthorCountResponse {
  /**
   * 响应状态码(1000=成功，-1000=失败，-99=异常）
   */
  code?: number;
  /**
   * 响应数据
   */
  data?: number;
  /**
   * 响应状态消息
   */
  message?: string;
  [property: string]: any;
}

export default ucApi;
